// Página filha da aba Listas (/contact-lists/:contactListId/contacts).
// Por ser filha, usa a topbar com breadcrumb do editor de fluxo
// ("← Listas / {nome} · N contatos") em vez da barra de abas. A contagem do
// breadcrumb vem de `contactsCount`, subquery de COUNT em
// ContactListService/ShowService.
// Estilos compartilhados em ../Campaigns/campaignPageStyles.
import React, {
  useState,
  useEffect,
  useReducer,
  useContext,
  useRef,
} from "react";

import { toast } from "react-toastify";
import { useParams, useHistory } from "react-router-dom";

import Button from "@material-ui/core/Button";
import IconButton from "@material-ui/core/IconButton";
import InputBase from "@material-ui/core/InputBase";
import Tooltip from "@material-ui/core/Tooltip";
import Typography from "@material-ui/core/Typography";
import CircularProgress from "@material-ui/core/CircularProgress";

import SearchIcon from "@material-ui/icons/Search";
import AddIcon from "@material-ui/icons/Add";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import PublishIcon from "@material-ui/icons/Publish";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import EditIcon from "@material-ui/icons/Edit";
import CloseIcon from "@material-ui/icons/Close";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import WarningIcon from "@material-ui/icons/ReportProblemOutlined";
import PeopleOutlineIcon from "@material-ui/icons/PeopleOutline";

import api from "../../services/api";
import ContactListItemModal from "../../components/ContactListItemModal";
import ImportacaoProgresso from "../../components/ImportacaoProgresso";
import AerosConfirmModal from "../../components/AerosConfirmModal";

import { i18n } from "../../translate/i18n";
import MainContainer from "../../components/MainContainer";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import { Can } from "../../components/Can";
import useContactLists from "../../hooks/useContactLists";

import planilhaExemplo from "../../assets/planilha.xlsx";
import ForbiddenPage from "../../components/ForbiddenPage";
import useCampaignPageStyles from "../Campaigns/campaignPageStyles";
import NumeroComTipo from "./NumeroComTipo";
import {
  whatsappValidity,
  initials,
  avatarColor,
  contactsCountLabel,
} from "../ContactLists/contactListRules";

const TABLE_GRID = "130px 1.8fr 1.2fr 1.6fr 110px";

const reducer = (state, action) => {
  if (action.type === "LOAD_CONTACTS") {
    const contacts = action.payload;
    const newContacts = [];

    contacts.forEach((contact) => {
      const contactIndex = state.findIndex((c) => c.id === contact.id);
      if (contactIndex !== -1) {
        state[contactIndex] = contact;
      } else {
        newContacts.push(contact);
      }
    });

    return [...state, ...newContacts];
  }

  if (action.type === "UPDATE_CONTACTS") {
    const contact = action.payload;
    const contactIndex = state.findIndex((c) => c.id === contact.id);

    if (contactIndex !== -1) {
      state[contactIndex] = contact;
      return [...state];
    } else {
      return [contact, ...state];
    }
  }

  if (action.type === "DELETE_CONTACT") {
    const contactId = action.payload;

    const contactIndex = state.findIndex((c) => c.id === contactId);
    if (contactIndex !== -1) {
      state.splice(contactIndex, 1);
    }
    return [...state];
  }

  if (action.type === "RESET") {
    return [];
  }
};

const ContactListItems = () => {
  const c = useCampaignPageStyles({ grid: TABLE_GRID });

  const { user, socket } = useContext(AuthContext);

  const { contactListId } = useParams();
  const history = useHistory();

  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [searchParam, setSearchParam] = useState("");
  const [contacts, dispatch] = useReducer(reducer, []);
  const [selectedContactId, setSelectedContactId] = useState(null);
  const [contactListItemModalOpen, setContactListItemModalOpen] =
    useState(false);
  const [deletingContact, setDeletingContact] = useState(null);
  const [importConfirmOpen, setImportConfirmOpen] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [contactList, setContactList] = useState({});
  // Progresso da importacao, alimentado pelos eventos de socket da lista.
  const [importacao, setImportacao] = useState({ ativa: false });
  const [linhasInvalidas, setLinhasInvalidas] = useState([]);
  const [linhasIgnoradas, setLinhasIgnoradas] = useState([]);
  const fileUploadRef = useRef(null);

  const { findById: findContactList } = useContactLists();

  useEffect(() => {
    findContactList(contactListId).then((data) => {
      setContactList(data);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contactListId]);

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [searchParam]);

  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      const fetchContacts = async () => {
        try {
          const { data } = await api.get(`contact-list-items`, {
            params: { searchParam, pageNumber, contactListId },
          });
          dispatch({ type: "LOAD_CONTACTS", payload: data.contacts });
          setHasMore(data.hasMore);
          setLoading(false);
        } catch (err) {
          toastError(err);
        }
      };
      fetchContacts();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [searchParam, pageNumber, contactListId]);

  useEffect(() => {
    const companyId = user.companyId;

    const onCompanyContactLists = (data) => {
      if (data.action === "update" || data.action === "create") {
        dispatch({ type: "UPDATE_CONTACTS", payload: data.record });
      }

      if (data.action === "delete") {
        dispatch({ type: "DELETE_CONTACT", payload: +data.id });
      }

      if (data.action === "reload") {
        dispatch({ type: "LOAD_CONTACTS", payload: data.records });
      }

      if (data.action === "import-start") {
        setImportacao({ ativa: true, processados: 0, total: data.total });
      }

      if (data.action === "import-progress") {
        setImportacao({
          ativa: true,
          processados: data.processados,
          total: data.total,
          criados: data.criados,
          ignorados: data.ignorados,
          invalidos: data.invalidos,
        });
      }

      if (data.action === "import-finish") {
        setImportacao({ ativa: false });
        // O toast de conclusao (com os totais) vem do listener global
        // ImportacaoNotificacao, que escuta o canal company-{id}-importacao em
        // qualquer tela — duplica-lo aqui mostrava dois avisos para quem
        // acompanhou a importacao sem sair da pagina. Ficam aqui so os avisos
        // que dependem do contexto da lista aberta.
        if (data.validacaoWhatsapp === "indisponivel") {
          toast.warn(i18n.t("contactListItems.toasts.noQrValidation"));
        }

        if (data.invalidos > 0) {
          setLinhasInvalidas(data.linhasInvalidas || []);
        }

        if (data.ignorados > 0) {
          setLinhasIgnoradas(data.linhasIgnoradas || []);
        }
      }

      if (data.action === "import-cancel") {
        // O toast (com os parciais) vem do listener global; aqui encerramos a
        // barra e mostramos o que deu tempo de apurar antes do cancelamento.
        setImportacao({ ativa: false });
        setLinhasInvalidas(data.linhasInvalidas || []);
        setLinhasIgnoradas(data.linhasIgnoradas || []);
      }

      if (data.action === "import-error") {
        // O toast do erro tambem vem do listener global; aqui so encerramos a
        // barra de progresso.
        setImportacao({ ativa: false });
      }
    };

    // Dois canais de proposito. O CRUD manual de contato emite sem sufixo; a
    // importacao emite com o id da lista, escopada a lista aberta. A tela so
    // escutava o primeiro, e por isso a importacao nunca aparecia em tempo real.
    const canalGeral = `company-${companyId}-ContactListItem`;
    const canalDaLista = `company-${companyId}-ContactListItem-${contactListId}`;

    socket.on(canalGeral, onCompanyContactLists);
    socket.on(canalDaLista, onCompanyContactLists);

    return () => {
      socket.off(canalGeral, onCompanyContactLists);
      socket.off(canalDaLista, onCompanyContactLists);
    };
  }, [contactListId]);

  const handleSearch = (event) => {
    setSearchParam(event.target.value.toLowerCase());
  };

  const handleOpenContactListItemModal = () => {
    setSelectedContactId(null);
    setContactListItemModalOpen(true);
  };

  const handleCloseContactListItemModal = () => {
    setSelectedContactId(null);
    setContactListItemModalOpen(false);
  };

  const hadleEditContact = (contactId) => {
    setSelectedContactId(contactId);
    setContactListItemModalOpen(true);
  };

  const handleDeleteContact = async (contactId) => {
    try {
      await api.delete(`/contact-list-items/${contactId}`);
      toast.success(i18n.t("contacts.toasts.deleted"));
    } catch (err) {
      toastError(err);
    }
    setDeletingContact(null);
    setSearchParam("");
    setPageNumber(1);
  };

  const handleImportContacts = async () => {
    try {
      const formData = new FormData();
      formData.append("file", fileUploadRef.current.files[0]);

      // Estado otimista: o backend responde 202 assim que aceita o arquivo e o
      // andamento chega por socket. Sem isto a tela ficava parada entre o clique
      // e o primeiro evento, sem nenhum sinal de que algo estava acontecendo.
      setLinhasInvalidas([]);
      setLinhasIgnoradas([]);
      setImportacao({ ativa: true, processados: 0, total: 0 });

      await api.request({
        url: `contact-lists/${contactListId}/upload`,
        method: "POST",
        data: formData,
      });
    } catch (err) {
      setImportacao({ ativa: false });
      toastError(err);
    }
  };

  const loadMore = () => {
    setPageNumber((prevState) => prevState + 1);
  };

  const handleScroll = (e) => {
    if (!hasMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight) {
      loadMore();
    }
  };

  const goToContactLists = () => {
    history.push("/contact-lists");
  };

  const renderNotice = (rows, kind, onDismiss) => {
    const titleKey =
      rows.length === 1
        ? `contactListItems.notices.${kind}One`
        : `contactListItems.notices.${kind}Other`;
    return (
      <div
        className={`${c.noticeCard} ${
          kind === "ignored" ? c.noticeInfo : c.noticeWarning
        }`}
      >
        {kind === "ignored" ? <InfoOutlinedIcon /> : <WarningIcon />}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className={c.noticeTitle}>
            {i18n.t(titleKey, { count: rows.length })}
          </div>
          <div className={c.noticeList}>
            {rows.map((item) => (
              <div key={item.linha}>
                {i18n.t("contactListItems.notices.line", { line: item.linha })}
                {item.nome ? ` (${item.nome})` : ""}:{" "}
                {kind === "ignored"
                  ? item.numero
                  : `“${item.original}” — ${item.motivo}`}
              </div>
            ))}
          </div>
        </div>
        <IconButton
          className={c.noticeClose}
          onClick={onDismiss}
          aria-label={i18n.t("contactListItems.notices.dismiss")}
        >
          <CloseIcon />
        </IconButton>
      </div>
    );
  };

  const renderEmpty = () => (
    <div className={c.emptyState}>
      <div className={c.emptyIcon}>
        <PeopleOutlineIcon />
      </div>
      <div className={c.emptyTitle}>
        {searchParam
          ? i18n.t("contactListItems.empty.noResults")
          : i18n.t("contactListItems.empty.title")}
      </div>
      {!searchParam && (
        <div className={c.emptyText}>{i18n.t("contactListItems.empty.text")}</div>
      )}
    </div>
  );

  if (user.profile === "user" && user?.showCampaign === "disabled") {
    return (
      <MainContainer>
        <ForbiddenPage />
      </MainContainer>
    );
  }

  const listCount = contactsCountLabel(contactList?.contactsCount);

  return (
    <MainContainer>
      <ContactListItemModal
        open={contactListItemModalOpen}
        onClose={handleCloseContactListItemModal}
        aria-labelledby="form-dialog-title"
        contactId={selectedContactId}
      />
      <AerosConfirmModal
        open={Boolean(deletingContact)}
        onClose={() => setDeletingContact(null)}
        onConfirm={() => handleDeleteContact(deletingContact.id)}
        icon={<DeleteOutlineIcon />}
        tone="danger"
        title={i18n.t("contactListItems.confirmDelete.title", {
          name: deletingContact?.name || "",
        })}
        description={i18n.t("contactListItems.confirmDelete.text")}
        confirmLabel={i18n.t("contactListItems.confirmDelete.confirm")}
      />
      <AerosConfirmModal
        open={importConfirmOpen}
        onClose={() => setImportConfirmOpen(false)}
        onConfirm={() => {
          setImportConfirmOpen(false);
          handleImportContacts();
        }}
        icon={<PublishIcon />}
        tone="info"
        primary
        title={i18n.t("contactListItems.confirmImport.title")}
        description={
          <>
            {i18n.t("contactListItems.confirmImport.text")}{" "}
            <a href={planilhaExemplo} download="planilha.xlsx">
              {i18n.t("contactListItems.downloadSample")}
            </a>
          </>
        }
        confirmLabel={i18n.t("contactListItems.confirmImport.confirm")}
      />

      <div className={c.pageHeader}>
        <button
          type="button"
          className={c.breadcrumbBack}
          onClick={goToContactLists}
        >
          <ArrowBackIcon />
          {i18n.t("contactListItems.buttons.lists")}
        </button>
        <span className={c.breadcrumbSep}>/</span>
        <Typography component="div" className={c.pageTitle}>
          {contactList.name || ""}
          {listCount && <span className={c.pageTitleCount}> · {listCount}</span>}
        </Typography>

        <div className={c.headerActions}>
          <InputBase
            className={c.searchPill}
            placeholder={i18n.t("contactListItems.searchPlaceholder")}
            type="search"
            value={searchParam}
            onChange={handleSearch}
            startAdornment={<SearchIcon />}
            inputProps={{
              "aria-label": i18n.t("contactListItems.searchPlaceholder"),
            }}
          />
          <Button
            className={c.outlineButton}
            disabled={importacao.ativa}
            startIcon={<PublishIcon />}
            onClick={() => {
              fileUploadRef.current.value = null;
              fileUploadRef.current.click();
            }}
          >
            {importacao.ativa
              ? i18n.t("contactListItems.importing")
              : i18n.t("contactListItems.buttons.import")}
          </Button>
          <Button
            variant="contained"
            color="primary"
            className={c.gradientButton}
            startIcon={<AddIcon />}
            onClick={handleOpenContactListItemModal}
          >
            {i18n.t("contactListItems.buttons.add")}
          </Button>
        </div>
      </div>

      <div className={c.pageBody} onScroll={handleScroll}>
        <input
          style={{ display: "none" }}
          id="upload"
          name="file"
          type="file"
          accept=".xls,.xlsx"
          onChange={() => setImportConfirmOpen(true)}
          ref={fileUploadRef}
        />

        {/* Barra padrao das importacoes; escuta o canal escopado da lista,
            que carrega os eventos import-* desta importacao. */}
        <ImportacaoProgresso
          canal={`company-${user.companyId}-ContactListItem-${contactListId}`}
          onCancelar={async () => {
            try {
              await api.post(`contact-lists/${contactListId}/upload/cancel`);
            } catch (err) {
              toastError(err);
            }
          }}
        />

        {linhasIgnoradas.length > 0 &&
          renderNotice(linhasIgnoradas, "ignored", () => setLinhasIgnoradas([]))}
        {linhasInvalidas.length > 0 &&
          renderNotice(linhasInvalidas, "invalid", () => setLinhasInvalidas([]))}

        <div className={c.tableCard}>
          <div className={c.tableHeadRow}>
            <div className={c.tableHeadCell} style={{ paddingLeft: 20 }}>
              {i18n.t("contactListItems.table.status")}
            </div>
            <div className={c.tableHeadCell}>
              {i18n.t("contactListItems.table.name")}
            </div>
            <div className={c.tableHeadCell}>
              {i18n.t("contactListItems.table.number")}
            </div>
            <div className={c.tableHeadCell}>
              {i18n.t("contactListItems.table.email")}
            </div>
            <div className={`${c.tableHeadCell} ${c.cellActionsHead}`}>
              {i18n.t("contactListItems.table.actions")}
            </div>
          </div>

          {contacts.map((contact) => {
            const validity = whatsappValidity(contact.isWhatsappValid);
            return (
              <div key={contact.id} className={c.tableRow}>
                <div className={c.tableCell} style={{ paddingLeft: 20 }}>
                  <Tooltip title={validity.title}>
                    <span className={`${c.statusPill} ${c[validity.tone]}`}>
                      <span className={c.statusDot} />
                      {validity.label}
                    </span>
                  </Tooltip>
                </div>
                <div className={c.nameCell} style={{ paddingLeft: 8 }}>
                  <div
                    className={c.initialAvatar}
                    style={{ backgroundColor: avatarColor(contact.id) }}
                  >
                    {initials(contact.name)}
                  </div>
                  <div className={c.nameText}>{contact.name}</div>
                </div>
                <div className={`${c.tableCell} ${c.cellEllipsis}`}>
                  <NumeroComTipo
                    number={contact.number}
                    numberType={contact.numberType}
                    classes={c}
                  />
                </div>
                <div
                  className={`${c.tableCell} ${c.cellMuted} ${c.cellEllipsis}`}
                  title={contact.email || ""}
                >
                  {contact.email || "—"}
                </div>
                <div className={`${c.tableCell} ${c.cellActions}`}>
                  <Tooltip title={i18n.t("contactListItems.actions.edit")}>
                    <IconButton
                      className={c.rowActionButton}
                      aria-label={i18n.t("contactListItems.actions.edit")}
                      onClick={() => hadleEditContact(contact.id)}
                    >
                      <EditIcon />
                    </IconButton>
                  </Tooltip>
                  <Can
                    role={user.profile}
                    perform="contacts-page:deleteContact"
                    yes={() => (
                      <Tooltip title={i18n.t("contactListItems.actions.delete")}>
                        <IconButton
                          className={`${c.rowActionButton} ${c.rowActionButtonDanger}`}
                          aria-label={i18n.t("contactListItems.actions.delete")}
                          onClick={() => setDeletingContact(contact)}
                        >
                          <DeleteOutlineIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                  />
                </div>
              </div>
            );
          })}

          {loading && (
            <div className={c.loadingRow}>
              <CircularProgress size={22} />
            </div>
          )}
          {!loading && contacts.length === 0 && renderEmpty()}
        </div>
      </div>
    </MainContainer>
  );
};

export default ContactListItems;
